import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import searchIcon from '../assets/search.png';
import filter from '../assets/filter-svgrepo-com.svg';
import download from '../assets/download-minimalistic-svgrepo-com.svg';
import add from '../assets/add-plus-svgrepo-com.svg';
import Vendors from '../components/Vendors';
import Customers from '../components/CustomersTable';

const tabs = ["Vendors", "Customers"];

// Filters for each Tab
const filtersByTab = {
  Vendors: [
    { key: "small", label: "Small Orders Quantity" },
    { key: "large", label: "Large Orders Quantity" },
    { key: "active", label: "Large Sales" },
    { key: "inactive", label: "Small Sales" },
  ],
  Customers: [
    { key: "active", label: "Active Customer" },
    { key: "inactive", label: "Inactive Customer" },
    { key: "premium", label: "Premium Customer" },
  ],
};

export default function Users() {
  const [tableData, setTableData] = useState([]);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    Vendors: [],
    Customers: [],
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedFromURL = queryParams.get("selected");

  const [activeTab, setActiveTab] = useState("Vendors");
  const navigate = useNavigate();

  // Detect tab from URL
  useEffect(() => {
    if (selectedFromURL) {
      const matchedTab = tabs.find(tab =>
        tab.toLowerCase().startsWith(selectedFromURL.toLowerCase())
      );
      if (matchedTab) setActiveTab(matchedTab);
    }
  }, [selectedFromURL]);

  // New user
  const handleNewUser = () => {
    navigate("/Users/add", { state: { fromUsers: true, activeTab } });
  };

  // Edit user
  const handleEditUser = (user) => {
    navigate(`/Users/edit-User/${user.id}`, {
      state: { user, activeTab },
    });
  };

  // Tab click
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    const key = tab.split(" ")[0].toLowerCase();
    navigate(`/Users?selected=${key}`);
  };

  // Handle filter selection (multi-select)
  const handleFilterSelect = (key) => {
    setFilterOptions(prev => {
      const current = prev[activeTab];
      if (current.includes(key)) return prev; // already selected
      return { ...prev, [activeTab]: [...current, key] };
    });
    setShowFilter(false);
  };

  // Remove individual filter
  const handleFilterRemove = (key) => {
    setFilterOptions(prev => {
      const current = prev[activeTab].filter(f => f !== key);
      return { ...prev, [activeTab]: current };
    });
  };

  // Clear all filters for active tab
  const handleClearFilters = () => {
    setFilterOptions(prev => ({ ...prev, [activeTab]: [] }));
  };

  // Apply search + filters dynamically
  const filteredData = tableData
    .filter(item => {
      const searchLower = search.toLowerCase();
      return (
        item.name?.toLowerCase().includes(searchLower) ||
        item.id?.toString().includes(searchLower)
      );
    })
    .filter(item => {
      const currentFilters = filterOptions[activeTab];
      if (!currentFilters || currentFilters.length === 0) return true;

      if (activeTab === "Vendors") {
        return currentFilters.every(f => {
          if (f === "small") return item.size === "S";
          if (f === "large") return item.size === "L";
          if (f === "active") return item.active === true;
          if (f === "inactive") return item.active === false;
          return true;
        });
      } else if (activeTab === "Customers") {
        return currentFilters.every(f => {
          if (f === "active") return item.active === true;
          if (f === "inactive") return item.active === false;
          if (f === "premium") return item.type === "premium";
          return true;
        });
      }
      return true;
    });

  // Tab content
  const tabContents = {
    "Vendors": <Vendors category="Vendors" tableData={filteredData} onData={setTableData} onEdit={handleEditUser} />,
    "Customers": <Customers category="Customers" tableData={filteredData} onData={setTableData} onEdit={handleEditUser} />,
  };

  // Export CSV
  const exportToCSV = () => {
  const filteredData = tableData;

  if (!filteredData || filteredData.length === 0) return;

  const headers = Object.keys(filteredData[0]).join(",");
  const rows = filteredData.map(row => Object.values(row).join(",")).join("\n");
  const csvContent = `${headers}\n${rows}`;

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `${activeTab}.csv`; 
  link.click();
};


  return (
    <div className="md:p-10 p-4 pb-4 text-base gap-4">
      {/* Page Titles */}
      <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">Users</h1>
      <p className="text-gray-600 pb-6 text-sm dark:text-gray-400">
        <Link to="/dashboard" className="hover:underline">Dashboard</Link>{" "}
        / Users
        {activeTab ? <span className="agarly-gradient-text font-bold"> / {activeTab}</span> : ""}
      </p>

      <div className="relative bg-white p-6 rounded-2xl shadow-md dark:bg-[#1A1A1B]">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-2 gap-4">
          {/* Search */}
          <div className="relative w-full md:w-[40%]">
            <input
              type="text"
              placeholder={`Search ${activeTab}`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="focus:outline-none focus:ring-1 focus:ring-gray-500 border-2 border-gray-400 rounded-xl pr-10 pl-3 py-2 text-l w-full dark:bg-[#1A1A1B]"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <img src={searchIcon} alt="search" className="w-5 h-5" />
            </span>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-2 md:gap-3">
            {/* Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilter(!showFilter)}
                className="flex border-2 border-gray-400 rounded-xl px-2 py-2 gap-2 text-sm items-center"
              >
                <p className="dark:text-gray-200">Filter</p>
                <img src={filter} alt="" className="w-5 h-5 md:w-6 h-6"/>
              </button>

              {showFilter && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-xl shadow-lg p-2 z-50 dark:bg-black dark:text-gray-200">
                  {filtersByTab[activeTab].map(f => (
                    <button
                      key={f.key}
                      className="block w-full text-left px-2 py-1 hover:bg-gray-100 dark:hover:bg-[#2D2D2D]"
                      onClick={() => handleFilterSelect(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                  <button
                    className="block w-full text-left px-2 py-1 hover:bg-gray-100 dark:hover:bg-[#2D2D2D]"
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </button>
                </div>
              )}
            </div>

            {/* Export */}
            <button onClick={exportToCSV} className="flex border-2 border-gray-400 rounded-xl px-2 py-2 gap-2 text-sm items-center">
              <p className="dark:text-gray-200">Export</p>
              <img src={download} alt="" className="w-5 h-5 md:w-6 h-6"/>
            </button>

            {/* Add New */}
            <button onClick={handleNewUser} className="flex border rounded-xl px-2 py-2 text-sm text-white items-center agarly-gradient-bg">
              <p>New User</p>
              <img src={add} alt="" className="w-6 h-6"/>
            </button>
          </div>
        </div>

        {/* Selected Filters Tags */}
        <div className="flex flex-wrap gap-2 mb-4 mt-4">
          {filterOptions[activeTab].map(f => {
            const label = filtersByTab[activeTab].find(flt => flt.key === f)?.label || f;
            return (
              <span key={f} className="flex items-center bg-blue-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {label}
                <button onClick={() => handleFilterRemove(f)} className="ml-1 font-bold">&times;</button>
              </span>
            )
          })}
        </div>

        {/* Tabs */}
        <div className="flex py-1 px-1 md:space-x-4 mb-4 border-2 border-gray-300 rounded-xl justify-between">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`py-1 w-[50%] text-sm rounded-lg ${
                activeTab === tab
                  ? "bg-blue-100 text-gray-600"
                  : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2D2D2D]"
              }`}
              onClick={() => handleTabClick(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab ? tabContents[activeTab] : "Please select a tab to display Users"}
        </div>
      </div>

      <div style={{ height: '40px' }}></div>
    </div>
  );
}
